import { MicrosoftIcon } from '@components/MicrosoftIcon';
import { ConfigContext } from '@context/ConfigContext';
import { getStorage, patchStorage } from '@services/localStorageService';
import { sendSSOAuthRequest } from '@services/postMessageService';
import { useCallback, useContext, useState } from 'react';

type TMicrosoftSignInProps = {
  buttonEdgeColor?: string;
  buttonTextColor?: string;
  buttonHoverColor?: string;
  hover?: boolean;
  onHoverChange?: (hover: boolean) => void;
};

export const MicrosoftSignIn = ({
  buttonHoverColor = '#0078D4',
  hover = false,
  onHoverChange,
}: TMicrosoftSignInProps) => {
  const { configId } = useContext(ConfigContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(() => {
    // Check if there's a stored SSO error from a previous verification failure
    const storedError = getStorage(configId).sso?.error ?? null;
    if (storedError) {
      // Clear it after reading so it only shows once
      const currentSso = getStorage(configId).sso;
      patchStorage(configId, {
        sso: { ...currentSso, error: null },
      });

      return storedError;
    }

    return null;
  });

  const handleOIDCLogin = useCallback(async () => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Send SSO request to parent window via postMessage
      const response = await sendSSOAuthRequest();

      patchStorage(configId, {
        sso: {
          accessToken: response.accessToken ?? null,
          refreshToken: response.refreshToken ?? null,
          idToken: response.idToken ?? null,
          upn: response.upn ?? null,
          cao: response.cao ?? null,
        },
      });

      // Dispatch custom event to notify same-window components
      window.dispatchEvent(new CustomEvent('sso-tokens-updated'));

      console.log(
        'SSO authentication successful:',
        JSON.stringify({ response }),
      );
    } catch (loginError) {
      console.error('SSO authentication error:', loginError);
      setError(
        loginError instanceof Error
          ? loginError.message
          : 'SSO authentication failed. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, configId]);

  // Microsoft brand blue color
  const microsoftBlue = '#0078D4';
  const hoverColor = hover ? microsoftBlue : buttonHoverColor;

  return (
    <div className='flex flex-col items-center'>
      <button
        type='button'
        className='button-outline flex items-center gap-2'
        disabled={isLoading}
        onMouseEnter={() => onHoverChange?.(true)}
        onMouseLeave={() => onHoverChange?.(false)}
        onClick={handleOIDCLogin}
        style={{
          color: hover ? hoverColor : '#5e5e5e',
          borderColor: hover ? hoverColor : '#8c8c8c',
          backgroundColor: 'white',
          opacity: isLoading ? 0.6 : 1,
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
        }}>
        <MicrosoftIcon />
        <span>Sign in with Microsoft</span>
      </button>
      {isLoading && (
        <div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
          Authenticating...
        </div>
      )}
      {error && (
        <div style={{ fontSize: '12px', marginTop: '8px', color: '#d32f2f' }}>
          {error}
        </div>
      )}
    </div>
  );
};
